import * as React from 'react';
import { cn } from '../../lib/utils';
import { cardVariants } from './variants';
import type {
  CardContextValue,
  CardProps,
  CardHeaderProps,
  CardTitleProps,
  CardDescriptionProps,
  CardContentProps,
  CardFooterProps,
} from './types';

const CardContext = React.createContext<CardContextValue>({});

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { className, children, topRightElement, variant, leftIcon, ...props },
    ref,
  ) => {
    const IconComponent = leftIcon ?? null;
    const hasLeftIcon = Boolean(IconComponent);

    return (
      <CardContext.Provider
        value={{ leftIcon: IconComponent ?? undefined, topRightElement }}
      >
        <div
          ref={ref}
          className={cn(
            cardVariants({ variant }),
            'eye:flex eye:border eye:border-grey-20',
            hasLeftIcon ? 'eye:items-start' : 'eye:flex-col',
            className,
          )}
          {...props}
        >
          {IconComponent ? (
            <div className='eye:hidden eye:shrink-0 eye:self-start eye:py-s eye:pl-m eye:md:flex'>
              <IconComponent
                className='eye:size-48 eye:rounded-full eye:bg-black eye:p-12 eye:text-white'
                aria-hidden='true'
                data-testid='card-left-icon'
              />
            </div>
          ) : null}
          {topRightElement ? (
            <div
              className='eye:absolute eye:top-[13px] eye:right-[13px]'
              data-testid='card-top-right-element'
            >
              {topRightElement}
            </div>
          ) : null}
          <div
            className={cn(
              'eye:flex eye:h-full eye:flex-col',
              hasLeftIcon && 'eye:min-w-0 eye:flex-1',
            )}
          >
            {children}
          </div>
        </div>
      </CardContext.Provider>
    );
  },
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => {
    const { topRightElement } = React.useContext(CardContext);

    return (
      <div
        ref={ref}
        className={cn(
          'eye:flex eye:flex-col eye:px-s eye:pt-s eye:pb-m eye:text-18',
          topRightElement && 'eye:pr-xl',
          className,
        )}
        {...props}
      />
    );
  },
);
CardHeader.displayName = 'CardHeader';

const headingTags = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
} as const;

const CardTitle = React.forwardRef<HTMLElement, CardTitleProps>(
  ({ className, children, headingLevel, ...props }, ref) => {
    const { leftIcon: LeftIconComponent } = React.useContext(CardContext);
    const Title = headingLevel === null ? 'div' : headingTags[headingLevel];

    return (
      <Title
        ref={ref}
        className={cn(
          'eye:tracking-tight eye:flex eye:items-center eye:text-18 eye:leading-24 eye:font-semibold',
          className,
        )}
        {...props}
      >
        {LeftIconComponent ? (
          <LeftIconComponent
            className='eye:icon-circle-black eye:mr-xs eye:md:hidden'
            aria-hidden='true'
            data-testid='card-left-icon'
          />
        ) : null}
        <span className='eye:flex-1'>{children}</span>
      </Title>
    );
  },
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'eye:mt-8 eye:text-16 eye:leading-24 eye:font-regular',
        className,
      )}
      {...props}
    />
  ),
);
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('eye:px-s eye:pb-m', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('eye:flex eye:items-center eye:px-s eye:pb-s', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
